/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React from 'react';
import { useSolarStore } from './store/useSolarStore';
import { OfflineBanner } from './components/ui/OfflineBanner';
import { Navbar } from './components/ui/Navbar';
import { Hero } from './components/hero/Hero';
import { SolarSimulator } from './components/hero/SolarSimulator';
import { EnvironmentalControls } from './components/controls/EnvironmentalControls';
import { DynamicROICalculator } from './components/calculator/DynamicROICalculator';
import { PackageCards } from './components/packages/PackageCards';
import { PackageComparison } from './components/packages/PackageComparison';
import { TrustProofBoard } from './components/trust/TrustProofBoard';
import { InstantQuoteModal } from './components/lead-capture/InstantQuoteModal';
import { LivePowerHub } from './components/dashboard/LivePowerHub';
import { FinancingCalculator } from './components/calculator/FinancingCalculator';
import { SiteAuditScheduler } from './components/lead-capture/SiteAuditScheduler';
import { AdminLeadsDashboard } from './components/admin/AdminLeadsDashboard';
import { GrowthOS } from './components/admin/growth/GrowthOS';
import { SolarChatCopilot } from './components/ai/SolarChatCopilot';
import { AILoadEstimator } from './components/ai/AILoadEstimator';
import { RoofAnalyzer } from './components/ai/RoofAnalyzer';
import { InstallationShowcase } from './components/gallery/InstallationShowcase';
import { FAQSection } from './components/faq/FAQSection';
import { Footer } from './components/footer/Footer';
import { useLandpageSettings } from './hooks/useLandpageSettings';
import { Zap, ShieldCheck, PhoneCall } from 'lucide-react';

export default function App() {
  const { activeTab, setIsLeadModalOpen, fetchPresets } = useSolarStore();
  const { settings } = useLandpageSettings();

  React.useEffect(() => {
    fetchPresets();
  }, [fetchPresets]);

  // Scroll to top automatically when changing tabs (e.g. logging into Admin)
  React.useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [activeTab]);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col font-sans selection:bg-emerald-500 selection:text-white">
      {/* Top Announcement Bar with Official Address & Phone */}
      <div className="bg-gradient-to-r from-emerald-950 via-slate-900 to-emerald-950 text-white border-b border-emerald-500/30 px-4 py-2.5 text-xs font-bold text-center flex flex-col sm:flex-row items-center justify-between gap-2 shadow-md">
        <div className="flex items-center gap-2 overflow-x-auto whitespace-nowrap text-[11px] sm:text-xs">
          <span className="flex items-center gap-1 bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 px-2 py-0.5 rounded">
            <Zap className="w-3 h-3 text-amber-300" /> Headquarters
          </span>
          <span className="text-emerald-200">
            📍 <strong>Address:</strong> {settings.address}
          </span>
        </div>

        <div className="flex items-center gap-3 text-[11px] sm:text-xs shrink-0">
          <a
            href={`tel:${settings.phone}`}
            className="flex items-center gap-1.5 text-amber-300 hover:text-white bg-amber-500/10 border border-amber-500/30 px-2.5 py-1 rounded-lg transition-colors"
          >
            <PhoneCall className="w-3 h-3 text-amber-400" />
            <span>Phone: <strong>{settings.phone}</strong></span>
          </a>
          <button
            onClick={() => setIsLeadModalOpen(true, 'Urgent Power Consultation')}
            className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 px-3 py-1 rounded-lg font-black transition-colors hidden md:inline-block"
          >
            Get Direct Quote
          </button>
        </div>
      </div>

      <OfflineBanner />

      <Navbar />

      <main className="flex-grow w-full max-w-none px-4 md:px-8 lg:px-12 py-6 space-y-12">
        {activeTab === 'calculator' && (
          <div className="space-y-12 pb-16">
            <Hero />
            <InstallationShowcase />
            <SolarSimulator />
            <AILoadEstimator />
            <EnvironmentalControls />
            <LivePowerHub />
            <DynamicROICalculator />
            <div className="pt-8 space-y-12">
              <PackageCards />
              <PackageComparison />
            </div>
            <TrustProofBoard />
            <FAQSection />
          </div>
        )}

        {activeTab === 'packages' && (
          <div className="py-8 pb-16 space-y-12">
            <PackageCards />
            <PackageComparison />
            <TrustProofBoard />
            <FAQSection />
          </div>
        )}

        {activeTab === 'dashboard' && (
          <div className="py-8 pb-16">
            <LivePowerHub />
          </div>
        )}

        {activeTab === 'financing' && (
          <div className="py-8 pb-16">
            <FinancingCalculator />
          </div>
        )}

        {activeTab === 'audit' && (
          <div className="py-8 pb-16">
            <SiteAuditScheduler />
          </div>
        )}

        {activeTab === 'admin' && (
          <div className="py-8 pb-16">
            <AdminLeadsDashboard initialView="leads" />
          </div>
        )}

        {activeTab === 'growth' && (
          <div className="py-8 pb-16">
            <AdminLeadsDashboard initialView="growth" />
          </div>
        )}

        {activeTab === 'ai-tools' && (
          <div className="py-8 pb-16">
            <AdminLeadsDashboard initialView="ai-tools" />
          </div>
        )}
      </main>

      <SolarChatCopilot />
      <InstantQuoteModal />
      <Footer />
    </div>
  );
}

